import React, { useState } from 'react';
import axios from 'axios';
import config from '../../config';
import CadreEtape, { champ, etiquette } from './CadreEtape';
import { boutonPrincipal } from './ParcoursInitial';

const API_URL = config.API_URL;

/**
 * Deuxième étape : créer le client.
 *
 * ## Les mots changent, pas l'objet
 *
 * Un annonceur crée ici sa propre société, une agence son premier client : c'est le même
 * `Client` en base, et c'est la réponse de l'étape précédente qui décide du libellé. Présenter
 * « votre premier client » à quelqu'un qui gère sa seule société le ferait hésiter sur ce qu'on
 * lui demande.
 *
 * ## Le doublon est vérifié ici
 *
 * La contrainte d'unicité `(userId, name)` n'est pas pré-vérifiée côté serveur : un nom déjà pris
 * y produit une violation de contrainte, rendue en « Erreur lors de la création du client »,
 * sans dire laquelle. Dans un parcours sans sortie, un refus muet est une impasse — d'où la
 * vérification préalable, qui nomme le problème. Ce cas n'est pas théorique : il se produit dès
 * qu'une personne reprend un parcours abandonné et ressaisit le même nom.
 *
 * ⚠️ `POST /api/client-management/clients` rend ses erreurs sous `error`, et non `message` —
 * la modale du Gestionnaire lit `message` et n'affiche donc jamais le motif du serveur.
 */
function EtapeClient({ token, etat, onAvance }) {
  const [nom, setNom] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);

  const agence = etat?.typeCompte === 'AGENCE';

  const creer = async (e) => {
    e.preventDefault();
    const propre = nom.trim();
    if (!propre) return;

    setEnvoi(true);
    setErreur(null);
    try {
      // Doublon : le dire avant que le serveur ne rende une erreur sans motif.
      const { data } = await axios.get(`${API_URL}/api/client-management/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const existants = Array.isArray(data) ? data : data?.clients || [];
      if (existants.some((c) => (c.name || '').toLowerCase() === propre.toLowerCase())) {
        setErreur(`« ${propre} » existe déjà. Choisissez un autre nom.`);
        setEnvoi(false);
        return;
      }

      await axios.post(`${API_URL}/api/client-management/clients`, { name: propre }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await onAvance();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Impossible de créer le client');
      setEnvoi(false);
    }
  };

  return (
    <CadreEtape
      titre={agence ? 'Votre premier client' : 'Votre société'}
      explication={agence
        ? 'Un client regroupe les sites et les comptes d’un même annonceur. Vous en ajouterez d’autres ensuite.'
        : 'Votre société regroupera vos sites et vos comptes publicitaires.'}
      erreur={erreur}
    >
      <form onSubmit={creer}>
        <label style={etiquette} htmlFor="nom-client">
          {agence ? 'Nom du client' : 'Nom de votre société'}
        </label>
        <input
          id="nom-client"
          value={nom}
          onChange={(e) => { setNom(e.target.value); setErreur(null); }}
          placeholder={agence ? 'Nom de l’entreprise' : 'Ma société'}
          autoFocus
          style={champ}
        />

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '22px' }}>
          <button
            type="submit"
            disabled={!nom.trim() || envoi}
            style={{
              ...boutonPrincipal,
              cursor: !nom.trim() || envoi ? 'not-allowed' : 'pointer',
              opacity: !nom.trim() || envoi ? 0.5 : 1
            }}
          >
            {envoi ? 'Création…' : 'Continuer'}
          </button>
        </div>
      </form>
    </CadreEtape>
  );
}

export default EtapeClient;
